import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Autocomplete,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UndoIcon from "@mui/icons-material/Undo";
import ReplayIcon from "@mui/icons-material/Replay";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CheckIcon from "@mui/icons-material/Check";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { toast } from "react-toastify";
import { defaultDateFormatter } from "../../Helper/functionHelper";
import TableLayout from "../layout/TableLayout";
import {
  NoRecordsFound,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Table";
import useModal from "../../hooks/useModal";
import useAuthUser from "../../hooks/useAuthUser";
import { priorityWithDays } from "../../constants/grievanceStatus";
import Button from "../ui/Button";
import EmployeeActionForm, {
  withdrawFormProps,
  reopenFormProps,
  acceptFormProps,
} from "./EmployeeActionForm";
import {
  withdrawGrievance,
  reopenGrievance,
  acceptGrievanceResolution,
  approveRejectGrievance,
  investigateGrievance,
  getUsersByRole,
} from "../../services/grievance";

// The list API no longer returns the raw status code, only status_label.
const SUBMITTED_LABEL = "Submitted";
const priorityOptions = ["Low", "Medium", "High"];
// pms_roles.id for "HR Executive" — Investigate's "Assign to Other" only
// ever offers HR Executives, so this is fixed rather than picked from a
// Role dropdown (same hardcoded role-id convention as the backend's
// HR_MANAGER_ROLE in grievance.service.js — no central roles constants file).
const HR_EXECUTIVE_ROLE_ID = 3;

const GrievanceList = ({ grievanceData = [], onFilter, activeFilters }) => {
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { canManageGrievance, loggedInUser } = useAuthUser();
  // Approve/Reject is for Admin and HR Manager. Investigate (Take Ownership /
  // Assign to Other) is offered per row via item.can_assign, which the backend
  // sets for HR Manager and Admin on any open grievance.
  const canApproveReject = canManageGrievance;

  // One shared menu, repositioned per row on open — avoids per-row anchor state.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const openActionMenu = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(item);
  };
  const closeActionMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };
  const runAction = (fn) => {
    closeActionMenu();
    fn();
  };

  const tableHeaders = [
    "Reference",
    "Department",
    "Category",
    "Sub Category",
    "Subject",
    "Priority",
    "Status",
    "Raised On",
    "Action",
  ];

  // Grievances can't be edited or deleted — the employee who raised one
  // withdraws it instead. The record stays (status "Withdrawn") for audit.
  const handleWithdraw = (id) => {
    showModal(
      "Withdraw Grievance",
      <EmployeeActionForm
        {...withdrawFormProps((reason) => withdrawGrievance(id, reason))}
        onDone={() => onFilter(activeFilters)}
      />
    );
  };

  // The employee accepts HR's resolution, which closes the case (item.can_accept).
  const handleAccept = (id) => {
    showModal(
      "Accept Resolution",
      <EmployeeActionForm
        {...acceptFormProps((comment) => acceptGrievanceResolution(id, comment))}
        onDone={() => onFilter(activeFilters)}
      />
    );
  };

  // The employee reopens a Resolved/Closed/Rejected grievance; the backend
  // decides via item.can_reopen.
  const handleReopen = (id) => {
    showModal(
      "Reopen Grievance",
      <EmployeeActionForm
        {...reopenFormProps((reason) => reopenGrievance(id, reason))}
        onDone={() => onFilter(activeFilters)}
      />
    );
  };

  const handleApprove = (item) => {
    const ApproveForm = () => {
      const [reason, setReason] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        setLoading(true);
        try {
          await approveRejectGrievance(item.id, "approve", { reason });
          toast.success("Grievance approved successfully");
          onFilter(activeFilters);
          hideModal();
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "Failed to approve grievance"
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSubmit} disabled={loading}>
              Approve
            </Button>
          </div>
        </div>
      );
    };

    showModal("Approve Grievance", <ApproveForm />);
  };

  const handleInvestigate = (item) => {
    const InvestigateForm = () => {
      // "own" = HR Manager takes ownership themselves; "other" = hand it to
      // an HR Executive picked from the dropdown below.
      const [mode, setMode] = useState("own");
      const [assignees, setAssignees] = useState([]);
      const [priority, setPriority] = useState(item.priority || "Medium");
      const [assignedTo, setAssignedTo] = useState(null);
      const [reason, setReason] = useState("");
      const [errors, setErrors] = useState({});
      const [loading, setLoading] = useState(false);
      const [assigneesLoading, setAssigneesLoading] = useState(false);

      // Reassignment vs a first-time assignment of an unowned grievance —
      // only the former needs a reason.
      const newOwnerId = mode === "own" ? loggedInUser?.id : assignedTo?.id;
      const isReassignment = !!item.assigned_to && item.assigned_to !== newOwnerId;

      useEffect(() => {
        if (mode !== "other") return;
        setAssigneesLoading(true);
        getUsersByRole(HR_EXECUTIVE_ROLE_ID)
          .then((res) => setAssignees(res.data?.data || []))
          .catch(() => toast.error("Failed to load HR Executive list"))
          .finally(() => setAssigneesLoading(false));
      }, [mode]);

      const selectMode = (newMode) => {
        setMode(newMode);
        setAssignedTo(null);
        setErrors((prev) => ({ ...prev, assignedTo: "" }));
      };

      const handleSubmit = async () => {
        const newErrors = {};
        if (!priority) newErrors.priority = "Priority is required.";
        if (mode === "other" && !assignedTo) {
          newErrors.assignedTo = "Please select an HR Executive.";
        }
        if (isReassignment && !reason.trim()) {
          newErrors.reason = "Reason is required when reassigning.";
        }
        if (Object.keys(newErrors).length) {
          setErrors(newErrors);
          return;
        }

        setLoading(true);
        try {
          const assigneeId = mode === "own" ? loggedInUser?.id : assignedTo.id;
          await investigateGrievance(item.id, {
            priority,
            assigned_to: assigneeId,
            reason: reason.trim() || undefined,
          });
          toast.success(
            mode === "own"
              ? "Ownership taken successfully"
              : "Grievance assigned to HR Executive"
          );
          onFilter(activeFilters);
          hideModal();
        } catch (error) {
          toast.error(
            error?.response?.data?.message ||
              "Failed to assign grievance for investigation"
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          {item.assigned_to_name && (
            <p className="mb-3" style={{ color: "#666" }}>
              Currently owned by: <strong>{item.assigned_to_name}</strong>
            </p>
          )}
          <div className="d-flex gap-2 mb-3">
            <Button
              type="button"
              variant={mode === "own" ? "success" : "secondary"}
              onClick={() => selectMode("own")}
            >
              Take Ownership
            </Button>
            <Button
              type="button"
              variant={mode === "other" ? "success" : "secondary"}
              onClick={() => selectMode("other")}
            >
              Assign to Other
            </Button>
          </div>

          <TextField
            select
            fullWidth
            variant="standard"
            label="Priority"
            className="mb-3"
            value={priority}
            onChange={(e) => {
              setPriority(e.target.value);
              setErrors((prev) => ({ ...prev, priority: "" }));
            }}
            error={!!errors.priority}
            helperText={errors.priority}
          >
            {priorityOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {priorityWithDays(opt)}
              </MenuItem>
            ))}
          </TextField>

          {mode === "other" && (
            <Autocomplete
              options={assignees}
              getOptionLabel={(option) => option.name || ""}
              value={assignedTo}
              onChange={(e, newValue) => {
                setAssignedTo(newValue);
                setErrors((prev) => ({ ...prev, assignedTo: "" }));
              }}
              loading={assigneesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="HR Executive"
                  error={!!errors.assignedTo}
                  helperText={errors.assignedTo}
                />
              )}
            />
          )}

          {isReassignment && (
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="standard"
              label="Reassignment Reason"
              className="mb-3 mt-3"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors((prev) => ({ ...prev, reason: "" }));
              }}
              error={!!errors.reason}
              helperText={errors.reason}
            />
          )}

          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSubmit} disabled={loading}>
              {mode === "own" ? "Take Ownership" : "Assign"}
            </Button>
          </div>
        </div>
      );
    };

    showModal("Investigate Grievance", <InvestigateForm />);
  };

  const handleReject = (id) => {
    const RejectForm = () => {
      const [reason, setReason] = useState("");
      const [error, setError] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        if (!reason.trim()) {
          setError("Reason is required for rejection.");
          return;
        }
        setLoading(true);
        try {
          await approveRejectGrievance(id, "reject", { reason });
          toast.success("Grievance rejected successfully");
          onFilter(activeFilters);
          hideModal();
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "Failed to reject grievance"
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleSubmit} disabled={loading}>
              Reject
            </Button>
          </div>
        </div>
      );
    };

    showModal("Reject Grievance", <RejectForm />);
  };

  return (
    <TableLayout>
      <Table className="table-col-freeze">
        <TableHeader>
          <TableRow>
            {tableHeaders.map((head, index) => (
              <TableHead
                key={index}
                className={
                  index === tableHeaders.length - 1 ? "last-col-freeze" : ""
                }
              >
                {head}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {grievanceData.length > 0 ? (
            grievanceData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.grievance_ref}</TableCell>
                <TableCell>{item.department}</TableCell>
                <TableCell>{item.category_name}</TableCell>
                <TableCell>{item.subcategory_name || "--"}</TableCell>
                <TableCell>{item.subject}</TableCell>
                <TableCell>{item.priority}</TableCell>
                <TableCell>
                  {item.status_label}
                  {item.is_overdue && (
                    <span
                      title={`Due by ${defaultDateFormatter(item.sla_due_date)}`}
                      style={{
                        marginLeft: "8px",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: "20px",
                        background: "#fde8e8",
                        color: "#c0392b",
                      }}
                    >
                      Overdue
                    </span>
                  )}
                </TableCell>
                <TableCell>{defaultDateFormatter(item.created_at)}</TableCell>
                <TableCell className="last-col-freeze">
                  <IconButton
                    size="small"
                    onClick={(e) => openActionMenu(e, item)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <NoRecordsFound colSpan={tableHeaders.length} />
          )}
        </TableBody>
      </Table>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeActionMenu}>
        {menuRow && [
          <MenuItem
            key="view"
            onClick={() => runAction(() => navigate(`/grievance/details/${menuRow.id}`))}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>,

          menuRow.can_withdraw && (
            <MenuItem
              key="withdraw"
              onClick={() => runAction(() => handleWithdraw(menuRow.id))}
            >
              <ListItemIcon>
                <UndoIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Withdraw</ListItemText>
            </MenuItem>
          ),

          menuRow.can_accept && (
            <MenuItem
              key="accept"
              onClick={() => runAction(() => handleAccept(menuRow.id))}
            >
              <ListItemIcon>
                <TaskAltIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Accept Resolution</ListItemText>
            </MenuItem>
          ),

          menuRow.can_reopen && (
            <MenuItem
              key="reopen"
              onClick={() => runAction(() => handleReopen(menuRow.id))}
            >
              <ListItemIcon>
                <ReplayIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Reopen</ListItemText>
            </MenuItem>
          ),

          canApproveReject && menuRow.status_label === SUBMITTED_LABEL && (
            <MenuItem key="approve" onClick={() => runAction(() => handleApprove(menuRow))}>
              <ListItemIcon>
                <CheckIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approve</ListItemText>
            </MenuItem>
          ),

          canApproveReject && menuRow.status_label === SUBMITTED_LABEL && (
            <MenuItem
              key="reject"
              onClick={() => runAction(() => handleReject(menuRow.id))}
            >
              <ListItemIcon>
                <DoNotDisturbAltIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Reject</ListItemText>
            </MenuItem>
          ),

          menuRow.can_assign && (
            <MenuItem
              key="investigate"
              onClick={() => runAction(() => handleInvestigate(menuRow))}
            >
              <ListItemIcon>
                <ManageSearchIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Investigate</ListItemText>
            </MenuItem>
          ),
        ]}
      </Menu>
    </TableLayout>
  );
};

export default GrievanceList;
